import { getSDK } from '@littlebird/sdk';
import { toast } from 'sonner';

import { withUIDelay } from '@/lib/animate';
import { logError } from '@/lib/errorLogging';
import { navigate } from '@/utils/navigation-service';

import { electronApis } from '../electron-api';

import { getMeeting, meetingStore } from './meetings.state';
import { type MeetingId } from './meetings.types';

type MeetingNoticeTab = 'notes' | 'summary';

interface MeetingNotice {
  meetingId: MeetingId;
  title: string;
  description: string;
  actionLabel: string;
  tab: MeetingNoticeTab;
  switchTabWhenViewing: boolean;
}

export function getMeetingSummaryNotificationId(meetingId: MeetingId) {
  return `meeting-summary:${meetingId}`;
}

function isMeetingActive(meetingId: MeetingId) {
  const { recording } = meetingStore;
  return (
    meetingStore.asRecordingOrStoppingMeeting?.meetingId === meetingId ||
    (recording.status === 'loading' && recording.meetingId === meetingId)
  );
}

export function notifySummaryReady(meetingId: MeetingId) {
  presentMeetingNotice({
    meetingId,
    title: 'Summary Ready',
    description: 'Your meeting summary is ready',
    actionLabel: 'View Summary',
    tab: 'summary',
    switchTabWhenViewing: true,
  });
}

/**
 * The notice reads the synced transcript timestamp, so it waits for a sync.
 * During a full bootstrap or a refill, a sync can take long to return
 * (ai-docs/architecture/frontend/sync.md says when it returns), so callers
 * must not await it: the recording lifecycle goes on.
 */
export function notifyDeferredSummaryReady(meetingId: MeetingId) {
  void getSDK()
    .syncNow()
    .catch((error) =>
      logError(
        error,
        '[meetings] Failed to sync before the deferred summary notice'
      )
    )
    .then(() => presentDeferredSummaryNotice(meetingId));
}

function presentDeferredSummaryNotice(meetingId: MeetingId) {
  const meeting = getMeeting(meetingId);
  if (!meeting?.first_transcript_at) return;
  // A later stop may have generated the summary while the sync was pending
  if (meeting.summary_status !== 'initial') return;

  presentMeetingNotice({
    meetingId,
    title: meeting.name ?? 'Meeting stopped',
    description: "Generate a summary when you're ready",
    actionLabel: 'View meeting',
    tab: 'notes',
    switchTabWhenViewing: false,
  });
}

function presentMeetingNotice({
  meetingId,
  title,
  description,
  actionLabel,
  tab,
  switchTabWhenViewing,
}: MeetingNotice) {
  if (isMeetingActive(meetingId)) return;

  const route = {
    to: '/meetings/$meetingId' as const,
    params: { meetingId },
    search: { tab },
  };

  if (document.visibilityState !== 'visible') {
    electronApis.lbNotifications.sendNotification({
      id: getMeetingSummaryNotificationId(meetingId),
      view: 'basic',
      sound: 'done',
      transient: true,
      payload: { title, message: description, route },
    });
    return;
  }

  if (meetingStore.meetingInViewId === meetingId) {
    if (switchTabWhenViewing) {
      withUIDelay(() => navigate({ ...route, replace: true }));
    }
    return;
  }

  toast.success(title, {
    description,
    action: { label: actionLabel, onClick: () => navigate(route) },
  });
}
